import { useCallback, useRef } from 'react'

function detectMacChrome() {
  if (typeof navigator === 'undefined') return false
  if (navigator.brave) return false

  const { userAgentData, userAgent = '' } = navigator
  if (userAgentData?.brands?.length) {
    return userAgentData.platform === 'macOS'
      && userAgentData.brands.some(({ brand }) => brand === 'Google Chrome')
  }

  return /Macintosh/.test(userAgent)
    && /Chrome\//.test(userAgent)
    && !/Edg\/|OPR\/|Opera|Brave|Vivaldi|YaBrowser|Electron/.test(userAgent)
}

const IS_MAC_CHROME = detectMacChrome()

const ACCENTS = {
  tilde: { combining: '\u0303', spacing: '\u02DC' },
  acute: { combining: '\u0301', spacing: '\u00B4' },
  grave: { combining: '\u0300', spacing: '`' },
  circumflex: { combining: '\u0302', spacing: '\u02C6' },
  diaeresis: { combining: '\u0308', spacing: '\u00A8' },
}

const OPTION_DEAD_KEYS = {
  KeyN: 'tilde',
  KeyE: 'acute',
  Backquote: 'grave',
  KeyI: 'circumflex',
  KeyU: 'diaeresis',
}

const ABNT2_DEAD_KEYS = {
  Quote: ['tilde', 'circumflex'],
  BracketLeft: ['acute', 'grave'],
}

const MODIFIER_KEYS = ['Shift', 'Alt', 'AltGraph', 'CapsLock', 'Control', 'Meta']

let isAbnt2Layout = false
if (IS_MAC_CHROME && navigator.keyboard?.getLayoutMap) {
  navigator.keyboard.getLayoutMap()
    .then((layout) => { isAbnt2Layout = layout.get('Quote') === '~' })
    .catch(() => {})
}

function deadKeyAccent(event) {
  if (event.altKey) return ACCENTS[OPTION_DEAD_KEYS[event.code]]
  if (isAbnt2Layout && ABNT2_DEAD_KEYS[event.code]) {
    return ACCENTS[ABNT2_DEAD_KEYS[event.code][event.shiftKey ? 1 : 0]]
  }
  return undefined
}

function compose(accent, key) {
  if (key === ' ') return accent.spacing
  const composed = `${key}${accent.combining}`.normalize('NFC')
  return composed.length === 1 ? composed : `${accent.spacing}${key}`
}

function insertText(input, text) {
  if (document.execCommand('insertText', false, text)) return
  input.setRangeText(text, input.selectionStart, input.selectionEnd, 'end')
  input.dispatchEvent(new Event('input', { bubbles: true }))
}

export default function useDeadKeys() {
  const pending = useRef(null)

  const onKeyDown = useCallback((event) => {
    const input = event.currentTarget

    if (event.key === 'Dead') {
      if (pending.current?.input === input) insertText(input, pending.current.accent.spacing)
      const accent = deadKeyAccent(event)
      pending.current = accent ? { accent, input } : null
      return
    }

    if (MODIFIER_KEYS.includes(event.key)) return

    const { current } = pending
    pending.current = null
    if (current?.input !== input) return
    if (event.key.length !== 1 || event.ctrlKey || event.metaKey) return
    if (event.nativeEvent.isComposing) return

    event.preventDefault()
    insertText(input, compose(current.accent, event.key))
  }, [])

  const onCompositionStart = useCallback(() => {
    pending.current = null
  }, [])

  if (!IS_MAC_CHROME) return {}

  return { onKeyDown, onCompositionStart }
}
